import type { TransferData } from '../../constants'
import { cleanText, firstText, normalizeDescriptionText, parseDescriptionFromHtml, readMeta } from '../../utils/parser'

function readRpiAttribute (...attributeNames: string[]): string {
  for (const name of attributeNames) {
    const byId = firstText([`#rpi-attribute-${name} .rpi-attribute-value`])
    if (byId) return byId

    const byDataName = firstText([`[data-rpi-attribute-name="${name}"] .rpi-attribute-value`])
    if (byDataName) return byDataName
  }

  return ''
}

function readDetailValue (labels: string[]): string {
  const candidates = Array.from(document.querySelectorAll<HTMLElement>('#detailBullets_feature_div li, #productDetailsTable tr, #detailBulletsWrapper_feature_div li'))

  for (const row of candidates) {
    const text = cleanText(row.textContent || '')
    if (!text) continue

    for (const label of labels) {
      const regex = new RegExp(`^${label}\\s*[:：]\\s*(.+)$`, 'i')
      const match = text.match(regex)
      if (match?.[1]) return cleanText(match[1])
    }
  }

  return ''
}

function parseTitle (): string {
  return firstText(['#productTitle', '#ebooksProductTitle']) || readMeta('og:title') || 'book'
}

function parseAuthors (): string {
  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('#bylineInfo a, .author a.a-link-normal'))
    .map((link) => cleanText(link.textContent || ''))
    .filter((text) => text && !/visit amazon/i.test(text) && !/format/i.test(text))

  if (links.length > 0) return Array.from(new Set(links)).join(', ')

  return ''
}

function parseIsbn (): string {
  const direct = readRpiAttribute('book_details-isbn13', 'book_details-isbn10')
  if (direct) {
    const digits = direct.replace(/[^0-9X]/gi, '')
    if (digits) return digits
  }

  const fromDetails = readDetailValue(['ISBN-13', 'ISBN-10', 'ISBN', 'ISBN-13（国際標準図書番号）', 'ISBN-10（国際標準図書番号）'])
  return fromDetails.replace(/[^0-9X]/gi, '')
}

function parseListeningLength (): string {
  const value = readRpiAttribute('audiobook_details-listening_length') || readDetailValue(['Listening Length', '再生時間'])
  if (!value) return ''

  const hm = value.match(/(\d{1,3})\s*(?:hours?|hrs?|h|時間)\s*(?:and\s*)?(\d{1,2})\s*(?:minutes?|mins?|m|分)/i)
  if (hm) return `${hm[1].padStart(2, '0')}:${hm[2].padStart(2, '0')}`

  const hOnly = value.match(/(\d{1,3})\s*(?:hours?|hrs?|h|時間)/i)
  if (hOnly) return `${hOnly[1].padStart(2, '0')}:00`

  const mOnly = value.match(/(\d{1,4})\s*(?:minutes?|mins?|m|分)/i)
  if (mOnly) {
    const totalMinutes = parseInt(mOnly[1], 10)
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  }

  return ''
}

function parsePages (): string {
  const listeningLength = parseListeningLength()
  if (listeningLength) return listeningLength

  const value = readRpiAttribute('book_details-ebook_pages', 'book_details-hardcover_pages', 'book_details-paperback_pages', 'book_details-fiona_pages', 'book_details-print_pages') || readDetailValue(['Print length', 'Pages', 'ページ数'])
  return value.match(/\b(\d{1,5})\b/)?.[1] || ''
}

function parsePublisher (): string {
  const value = readRpiAttribute('book_details-publisher') || readDetailValue(['Publisher', '出版社'])
  return cleanText(value.replace(/\([^)]*\)/g, ''))
}

function parsePublicationDate (): string {
  return readRpiAttribute('book_details-publication_date') || readDetailValue(['Publication date', '発売日', '出版日'])
}

function parseLanguage (): string {
  return readRpiAttribute('language', 'book_details-language') || readDetailValue(['Language', '言語'])
}

function parseDescription (): string {
  const htmlCandidates = ['#bookDescription_feature_div noscript', '#bookDescription_feature_div .a-expander-content', '#bookDescription_feature_div']
  for (const selector of htmlCandidates) {
    const el = document.querySelector<HTMLElement>(selector)
    if (!el) continue

    const value = parseDescriptionFromHtml(el.innerHTML)
    if (value) return value
  }

  const paragraphText = Array.from(document.querySelectorAll<HTMLElement>('#productDescription p'))
    .map((el) => normalizeDescriptionText(el.textContent || ''))
    .filter(Boolean)
    .join('\n\n')
  if (paragraphText) return paragraphText

  return normalizeDescriptionText(readMeta('description', 'og:description'))
}

function parseCoverUrl (): string {
  const image = document.querySelector<HTMLImageElement>('#landingImage, #imgBlkFront, #ebooksImgBlkFront')
  if (image?.src) return image.src
  return readMeta('og:image')
}

function parseFormat (): string {
  const formatText = firstText(['#productSubtitle', '#formatSelector', '#tmmSwatches']).toLowerCase()
  if (formatText.includes('hardcover') || formatText.includes('ハードカバー')) return 'hardcover'
  if (formatText.includes('paperback') || formatText.includes('単行本') || formatText.includes('文庫')) return 'paperback'
  if (formatText.includes('kindle') || formatText.includes('ebook') || formatText.includes('電子書籍')) return 'digital'
  if (formatText.includes('audio') || formatText.includes('audible')) return 'audio'

  return ''
}

export function parseAmazonPage (): TransferData {
  return {
    title: parseTitle(),
    originalTitle: '',
    author: parseAuthors(),
    language: parseLanguage(),
    publisher: parsePublisher(),
    isbn: parseIsbn(),
    pages: parsePages(),
    pubDate: parsePublicationDate(),
    format: parseFormat(),
    desc: parseDescription(),
    rating: '',
    review: '',
    coverUrl: parseCoverUrl()
  }
}
